import posthog from 'posthog-js';
import { config } from '../config';
import isElectron from '../utils/isElectron';
import ReleaseChannelService from './ReleaseChannel.service';

class PostHogService {
  private static initialized = false;
  private static initStarted = false;
  private static readonly environment = isElectron() ? 'electron' : 'web';

  static init() {
    if (this.initStarted || !config.POSTHOG_API_KEY) {
      return;
    }
    this.initStarted = true;
    try {
      posthog.init(config.POSTHOG_API_KEY, {
        api_host: config.POSTHOG_HOST,
        capture_pageview: false,
        loaded: (posthog) => {
          PostHogService.initialized = true;
          console.log('[PostHog] Loaded and ready!');
          if (process.env.NODE_ENV === 'development') {
            console.log('PostHog initialized:', posthog);
          }
        },
        capture_pageleave: true,
        autocapture: true,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-mask]',
        },
      });
      posthog.register({
        app_environment: PostHogService.environment,
      });
      PostHogService.registerReleaseChannel();
      setTimeout(() => {
        if (!PostHogService.initialized && posthog) {
          try {
            if (typeof posthog.capture === 'function') {
              PostHogService.initialized = true;
            }
          } catch (e) {
            console.warn('PostHog still not ready after timeout');
          }
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
      this.initStarted = false;
    }
  }

  static identify(userId: string, properties?: Record<string, any>) {
    if (!this.isInitialized() || !config.POSTHOG_API_KEY) {
      return;
    }

    try {
      posthog.identify(userId, {
        app_environment: PostHogService.environment,
        ...properties,
      });
    } catch (error) {
      console.error('Failed to identify user in PostHog:', error);
    }
  }

  static reset() {
    if (!this.isInitialized()) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('Failed to reset PostHog:', error);
    }
  }

  static capture(eventName: string, properties?: Record<string, any>) {
    if (!this.isInitialized() || !config.POSTHOG_API_KEY) {
      return;
    }

    try {
      posthog.capture(eventName, properties);
      console.log('[PostHog] Event captured:', eventName);
    } catch (error) {
      console.error('Failed to capture event in PostHog:', error);
    }
  }

  static capturePageview(url: string, properties?: Record<string, any>): void {
    if (!this.isInitialized() || !config.POSTHOG_API_KEY) {
      return;
    }

    try {
      posthog.capture('$pageview', {
        $current_url: url,
        app_environment: PostHogService.environment,
        ...properties,
      });
    } catch (error) {
      console.error('Failed to capture PostHog pageview:', error);
    }
  }

  static setPersonProperties(properties: Record<string, any>) {
    if (!this.isInitialized() || !config.POSTHOG_API_KEY) {
      return;
    }

    try {
      posthog.setPersonProperties({
        app_environment: PostHogService.environment,
        ...properties,
      });
    } catch (error) {
      console.error('Failed to set person properties in PostHog:', error);
    }
  }

  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Publishes the release channel to PostHog so feature flags can target dev
   * first and be widened to next and production without shipping a build.
   * setPersonPropertiesForFlags is what makes the channel usable as a flag
   * condition; register only attaches it to outgoing events.
   */
  private static async registerReleaseChannel() {
    try {
      const releaseChannel = await ReleaseChannelService.resolve();
      posthog.register({ release_channel: releaseChannel });
      posthog.setPersonPropertiesForFlags({ release_channel: releaseChannel });
    } catch (error) {
      console.error('Failed to register release channel in PostHog:', error);
    }
  }
}

export default PostHogService;
